/**
 * iter315 — Canonical "is this a live (publicly-visible) ad?" predicate
 * for client-side filtering.
 *
 * Single source of truth so the Home discovery tabs, the For-You feed,
 * and any future client-side filter all agree with what /api/ads
 * actually returns.
 *
 * Why the duplication-of-truth bug existed:
 *   The backend canonical "public visible ads" query
 *   (_build_real_marketplace_ad_query in server.py) stamps ads with
 *   `status: "active"`. The frontend discovery feeds were filtering
 *   `status === "published"`, so they silently rejected EVERY real ad
 *   and the Home tabs (Trending / Near Me / Top Search) collapsed to
 *   the "No ads are live yet" empty state even though the marketplace
 *   was clearly non-empty.
 *
 * Rules:
 *   - Accept anything coming out of /api/ads (it's already filtered
 *     publicly-live on the backend).
 *   - Defensive: reject obviously dead states (sold, deleted, hidden,
 *     rejected, pending, draft) just in case a stale local-cache ever
 *     re-feeds these into a client-side pipeline.
 *   - Treat missing `status` as live — many older docs predate the
 *     status field and the backend already wouldn't return them if
 *     they weren't live.
 */

// Lowercase set so callers don't have to worry about case sensitivity.
const DEAD_STATUSES = new Set([
  'sold',
  'deleted',
  'archived',
  'hidden',
  'rejected',
  'pending',
  'pending_review',
  'draft',
  'expired',
  'paused',
  'unpublished',
]);

/**
 * @param {object} item — an ad-shaped object
 * @returns {boolean}
 */
export function isLiveListing(item) {
  if (!item) return false;
  const status = (item.status || '').toString().toLowerCase();
  if (!status) return true; // legacy docs predate the status field
  if (DEAD_STATUSES.has(status)) return false;
  // Anything else (`active`, `published`, `live`, future-compat values)
  // is treated as live. This intentionally mirrors what /api/ads already
  // hands us so the client never silently drops real listings.
  return true;
}

/**
 * Convenience filter — drop dead items, keep originals' order.
 * @param {Array<object>} list
 * @returns {Array<object>}
 */
export function filterLiveListings(list) {
  if (!Array.isArray(list)) return [];
  return list.filter(isLiveListing);
}
